/**
 * LobbyBar Component for Poker Reborn
 *
 * Top navigation bar in the lobby with:
 * - PlayerInfoWidget (avatar, name, balance)
 * - Fullscreen button
 * - Rotate button
 * - Sound toggle button
 * - Settings button
 *
 * @see docs/screens/lobby/LOBBY_BAR.md - Visual specs, dimensions, behavior
 */

import { Container, Graphics } from 'pixi.js';
import { Button } from '@/components/common/Button';
import { PlayerInfoWidget } from '@/components/common/PlayerInfoWidget';
import { audioService } from '@/services/AudioService';
import { storageService } from '@/services/StorageService';
import { avatarService } from '@/services/AvatarService';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Bar dimensions */
  bar: {
    height: 48,
    heightMobile: 44,
    backgroundColor: 0x000000,
    backgroundAlpha: 0.6,
    borderColor: 0x2a2a40,
    borderAlpha: 0.5,
    borderWidth: 1,
    padding: 12,
    zIndex: 100,
  },

  /** Button dimensions */
  button: {
    width: 100,
    widthMobile: 36,
    widthRotate: 80,
    widthRotateMobile: 36,
    height: 36,
    fontSize: 14,
    gap: 8, // Gap between buttons
  },

  /** Responsive breakpoints */
  breakpoints: {
    mobile: 480,
  },
};

// =============================================================================
// LOBBYBAR COMPONENT
// =============================================================================

export class LobbyBar extends Container {
  private screenWidth: number;
  private screenHeight: number;

  // Components
  private background: Graphics;
  private playerInfo: PlayerInfoWidget;
  private fullscreenButton: Button;
  private rotateButton: Button;
  private soundButton: Button;
  private settingsButton: Button;
  private avatarBadge: Graphics | null = null;

  // State
  private currentRotation: number = 0;
  private isFullscreen: boolean = false;

  // Callbacks
  public onFullscreen?: () => void;
  public onRotate?: () => void;
  public onSettings?: () => void;
  public onProfileClick?: () => void;

  constructor(width: number, height: number) {
    super();
    this.screenWidth = width;
    this.screenHeight = height;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create PlayerInfoWidget (avatar + name + balance)
    this.playerInfo = new PlayerInfoWidget({
      screenWidth: width,
      name: '',
    });
    this.playerInfo.onProfileClick = () => {
      this.onProfileClick?.();
    };
    this.addChild(this.playerInfo);

    // Create fullscreen button
    this.fullscreenButton = new Button({
      label: 'Fullscreen',
      width: STYLES.button.width,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.fullscreenButton.onClick = () => { this.handleFullscreenClick(); };
    this.addChild(this.fullscreenButton);

    // Create rotate button
    this.rotateButton = new Button({
      label: '0',
      width: STYLES.button.widthRotate,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.rotateButton.onClick = () => { this.handleRotateClick(); };
    this.addChild(this.rotateButton);

    // Create sound toggle button
    const isMuted = audioService.isMuted();
    this.soundButton = new Button({
      label: isMuted ? 'Sound Off' : 'Sound On',
      width: STYLES.button.width,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.soundButton.onClick = () => { this.handleSoundToggle(); };
    this.addChild(this.soundButton);

    // Create settings button
    this.settingsButton = new Button({
      label: 'Settings',
      width: STYLES.button.width,
      height: STYLES.button.height,
      style: {
        fontSize: STYLES.button.fontSize,
      },
    });
    this.settingsButton.onClick = () => this.onSettings?.();
    this.addChild(this.settingsButton);

    // Subscribe to mute state changes
    audioService.on('muteChange', ({ muted }) => {
      this.updateSoundButton(muted);
    });

    // Show avatar nudge badge if no avatar selected
    if (!avatarService.hasAvatar()) {
      this.showAvatarBadge();
    }
    avatarService.on('avatarChanged', this.onAvatarChanged);

    // Initial layout
    this.layout();
  }

  /**
   * Resize the lobby bar
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.playerInfo.resize(width);
    this.layout();
  }

  /**
   * Set title text (player name)
   */
  setTitle(text: string): void {
    this.playerInfo.setName(text);
  }

  /**
   * Get current title text
   */
  getTitle(): string {
    return this.playerInfo.getName();
  }

  /**
   * Set rotation angle display
   */
  setRotation(angle: number): void {
    this.currentRotation = angle;
    this.updateRotateButton();
  }

  /**
   * Set fullscreen state display
   */
  setFullscreen(isFullscreen: boolean): void {
    this.isFullscreen = isFullscreen;
    this.updateFullscreenButton();
  }

  /**
   * Get the bar height for layout calculations
   */
  getBarHeight(): number {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    return isMobile ? STYLES.bar.heightMobile : STYLES.bar.height;
  }

  /**
   * Clean up
   */
  destroy(): void {
    avatarService.off('avatarChanged', this.onAvatarChanged);
    this.playerInfo.destroy();
    super.destroy();
  }

  // ===========================================================================
  // AVATAR BADGE
  // ===========================================================================

  private onAvatarChanged = (): void => {
    this.hideAvatarBadge();
  };

  private showAvatarBadge(): void {
    if (this.avatarBadge) return;

    // Gold glowing dot on the avatar area (top-right of avatar circle)
    this.avatarBadge = new Graphics();
    this.avatarBadge.circle(0, 0, 5);
    this.avatarBadge.fill(0xffd700);
    // Position near top-right of avatar (avatar is at padding + 32/2 = padding + 16)
    this.avatarBadge.x = STYLES.bar.padding + 28;
    this.avatarBadge.y = this.getBarHeight() / 2 - 12;
    this.addChild(this.avatarBadge);
  }

  private hideAvatarBadge(): void {
    if (this.avatarBadge) {
      this.removeChild(this.avatarBadge);
      this.avatarBadge.destroy();
      this.avatarBadge = null;
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private layout(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    const barHeight = isMobile ? STYLES.bar.heightMobile : STYLES.bar.height;

    // Draw background
    this.background.clear();
    this.background.rect(0, 0, this.screenWidth, barHeight);
    this.background.fill({ color: STYLES.bar.backgroundColor, alpha: STYLES.bar.backgroundAlpha });

    // Draw bottom border
    this.background.rect(0, barHeight - STYLES.bar.borderWidth, this.screenWidth, STYLES.bar.borderWidth);
    this.background.fill({ color: STYLES.bar.borderColor, alpha: STYLES.bar.borderAlpha });

    const buttonY = (barHeight - STYLES.button.height) / 2;

    // Update button sizes for mobile/desktop
    const isMuted = audioService.isMuted();
    if (isMobile) {
      this.fullscreenButton.label = this.isFullscreen ? 'X' : '^';
      this.fullscreenButton.resize(STYLES.button.widthMobile, STYLES.button.height);
      this.rotateButton.label = `${this.currentRotation}`;
      this.rotateButton.resize(STYLES.button.widthRotateMobile, STYLES.button.height);
      this.soundButton.label = isMuted ? 'M' : 'S';
      this.soundButton.resize(STYLES.button.widthMobile, STYLES.button.height);
      this.settingsButton.label = 'G';
      this.settingsButton.resize(STYLES.button.widthMobile, STYLES.button.height);
    } else {
      this.fullscreenButton.label = this.isFullscreen ? 'Exit FS' : 'Fullscreen';
      this.fullscreenButton.resize(STYLES.button.width, STYLES.button.height);
      this.rotateButton.label = `${this.currentRotation}`;
      this.rotateButton.resize(STYLES.button.widthRotate, STYLES.button.height);
      this.soundButton.label = isMuted ? 'Sound Off' : 'Sound On';
      this.soundButton.resize(STYLES.button.width, STYLES.button.height);
      this.settingsButton.label = 'Settings';
      this.settingsButton.resize(STYLES.button.width, STYLES.button.height);
    }

    // Position PlayerInfoWidget (left side, vertically centered)
    this.playerInfo.x = STYLES.bar.padding;
    this.playerInfo.y = barHeight / 2;

    // Position buttons from right edge (right-to-left)
    // Order: Settings -> Sound -> Rotate -> Fullscreen
    const settingsWidth = isMobile ? STYLES.button.widthMobile : STYLES.button.width;
    const soundWidth = isMobile ? STYLES.button.widthMobile : STYLES.button.width;
    const rotateWidth = isMobile ? STYLES.button.widthRotateMobile : STYLES.button.widthRotate;
    const fullscreenWidth = isMobile ? STYLES.button.widthMobile : STYLES.button.width;

    // Settings button (rightmost)
    this.settingsButton.x = this.screenWidth - settingsWidth - STYLES.bar.padding;
    this.settingsButton.y = buttonY;

    // Sound button (left of settings)
    this.soundButton.x = this.settingsButton.x - soundWidth - STYLES.button.gap;
    this.soundButton.y = buttonY;

    // Rotate button (left of sound)
    this.rotateButton.x = this.soundButton.x - rotateWidth - STYLES.button.gap;
    this.rotateButton.y = buttonY;

    // Fullscreen button (left of rotate)
    this.fullscreenButton.x = this.rotateButton.x - fullscreenWidth - STYLES.button.gap;
    this.fullscreenButton.y = buttonY;
  }

  private handleFullscreenClick(): void {
    this.onFullscreen?.();
  }

  private handleRotateClick(): void {
    this.onRotate?.();
  }

  private handleSoundToggle(): void {
    const newMuted = audioService.toggleMute();
    storageService.setSoundSettings({ muted: newMuted });
    this.updateSoundButton(newMuted);
  }

  private updateSoundButton(muted: boolean): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    if (isMobile) {
      this.soundButton.label = muted ? 'M' : 'S';
    } else {
      this.soundButton.label = muted ? 'Sound Off' : 'Sound On';
    }
  }

  private updateRotateButton(): void {
    this.rotateButton.label = `${this.currentRotation}`;
  }

  private updateFullscreenButton(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    if (isMobile) {
      this.fullscreenButton.label = this.isFullscreen ? 'X' : '^';
    } else {
      this.fullscreenButton.label = this.isFullscreen ? 'Exit FS' : 'Fullscreen';
    }
  }
}
